/**
 * Labstep
 *
 * @module services/bugsnag
 * @desc BugsnagService
 */

import type { Client, NotifiableError } from '@bugsnag/browser';
import Bugsnag, { type BrowserConfig } from '@bugsnag/js';
import BugsnagPluginReact, {
  type BugsnagPluginReactResult,
} from '@bugsnag/plugin-react';
import { APP_VERSION } from 'labstep-web/constants/version';
import type { User } from 'labstep-web/models/user.model';
import { LogRocketService } from 'labstep-web/services/logrocket.service';
import React from 'react';
import { configService } from './config.service';

/** Truncate payload bigger than 10k characters */
const MAX_PAYLOAD_SIZE = 10000;

function instanceOfNotifiableError(
  object: unknown,
): object is NotifiableError {
  return (
    typeof object === 'string' ||
    object instanceof Error ||
    (typeof object === 'object' &&
      object !== null &&
      (('message' in object && 'name' in object) ||
        ('errorClass' in object && 'errorMessage' in object)))
  );
}

export class BugsnagService {
  public static getInstance(): BugsnagService {
    if (!BugsnagService.instance) {
      BugsnagService.instance = new BugsnagService();
    }
    return BugsnagService.instance;
  }

  private static instance: BugsnagService;

  private bugsnagClient?: Client;

  private constructor() {
    const { bugsnagApiKey, labstepEnv, nodeEnv } = configService;
    if (
      !bugsnagApiKey ||
      !labstepEnv ||
      nodeEnv === 'test' ||
      (labstepEnv !== 'production' && labstepEnv !== 'staging')
    ) {
      return;
    }
    const bugsnagOptions: BrowserConfig = {
      apiKey: bugsnagApiKey,
      plugins: [new BugsnagPluginReact(React)],
      releaseStage: labstepEnv,
      appVersion: APP_VERSION,
      collectUserIp: false,
    };
    const bugsnagClient = Bugsnag.start(bugsnagOptions);
    bugsnagClient.leaveBreadcrumb('Starting the web application');
    this.bugsnagClient = bugsnagClient;
  }

  public addMetadata(
    section: string,
    values: Record<string, unknown>,
  ): void {
    if (!this.bugsnagClient) {
      return;
    }
    if (values.payload) {
      const payload = JSON.stringify(values.payload);
      if (payload.length > MAX_PAYLOAD_SIZE) {
        // FIXME no-param-reassign
        values.payload = {
          truncated_payload: payload.substring(0, MAX_PAYLOAD_SIZE),
        };
      }
    }
    this.bugsnagClient.addMetadata(section, values);
  }

  public notify(
    exception: unknown,
    metadatas?: Record<string, unknown> | null,
    severity?: string,
    groupingHash?: string,
  ): void {
    console.error(exception, metadatas);

    if (!this.bugsnagClient) {
      return;
    }
    if (!instanceOfNotifiableError(exception)) {
      return;
    }
    this.bugsnagClient.notify(exception, (event: any) => {
      if (metadatas) {
        Object.keys(metadatas).forEach((key) => {
          event.addMetadata(key, metadatas[key]);
        });
      }
      if (severity) {
        // FIXME no-param-reassign
        event.severity = severity;
      }
      if (groupingHash) {
        // FIXME no-param-reassign
        event.groupingHash = groupingHash;
      }
    });
    if (exception instanceof Error) {
      LogRocketService.captureException(exception);
    }
  }

  public leaveBreadcrumb(
    label: string,
    breadcrumb: Record<string, unknown>,
  ): void {
    if (!this.bugsnagClient) {
      return;
    }
    this.bugsnagClient.leaveBreadcrumb(label, breadcrumb);
  }

  public identify(user: User): void {
    if (!this.bugsnagClient) {
      return;
    }
    const { id, name, username, guid } = user;
    try {
      this.bugsnagClient.setUser(guid);
    } catch (e) {
      console.error('bugsnagClient.setUser', e);
    }
    this.addMetadata('user', {
      userId: id,
      name,
      username,
      email: username,
    });
  }

  public getReactPlugin(): BugsnagPluginReactResult | undefined {
    if (!this.bugsnagClient) {
      return undefined;
    }
    return this.bugsnagClient.getPlugin('react');
  }
}

const bugsnagServiceInstance = BugsnagService.getInstance();

export default bugsnagServiceInstance;
